import { useState, useCallback, useRef } from 'react';
import { LegalDocument } from '@/schemas/schema';

const MAX_HISTORY = 20;

interface DocumentHistory {
  current: LegalDocument;
  canUndo: boolean;
  canRedo: boolean;
  push: (doc: LegalDocument) => void;
  undo: () => LegalDocument | null;
  redo: () => LegalDocument | null;
  reset: (doc: LegalDocument) => void;
}

/**
 * Manages an undo/redo history stack for LegalDocument edits.
 * Each push() snapshots the current state before applying the new one.
 * Capped at MAX_HISTORY entries to bound memory usage.
 *
 * Ported from the B2C app's useDocumentHistory. The Blocks-specific
 * persistence (sessionStorage snapshot + UDS write) is layered on top by
 * the caller — this hook only owns in-memory undo/redo.
 */
export function useDocumentHistory(initial: LegalDocument): DocumentHistory {
  const [current, setCurrent] = useState<LegalDocument>(initial);
  const pastRef = useRef<LegalDocument[]>([]);
  const futureRef = useRef<LegalDocument[]>([]);

  // Force a re-render when stack lengths change (for canUndo/canRedo)
  const [, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const push = useCallback(
    (doc: LegalDocument) => {
      setCurrent((prev) => {
        pastRef.current = [...pastRef.current.slice(-(MAX_HISTORY - 1)), prev];
        futureRef.current = [];
        bump();
        return doc;
      });
    },
    [bump]
  );

  const undo = useCallback((): LegalDocument | null => {
    if (pastRef.current.length === 0) return null;

    let restored: LegalDocument | null = null;
    setCurrent((prev) => {
      const past = [...pastRef.current];
      const previous = past.pop()!;
      pastRef.current = past;
      futureRef.current = [prev, ...futureRef.current];
      restored = previous;
      bump();
      return previous;
    });
    return restored;
  }, [bump]);

  const redo = useCallback((): LegalDocument | null => {
    if (futureRef.current.length === 0) return null;

    let restored: LegalDocument | null = null;
    setCurrent((prev) => {
      const future = [...futureRef.current];
      const next = future.shift()!;
      futureRef.current = future;
      pastRef.current = [...pastRef.current, prev];
      restored = next;
      bump();
      return next;
    });
    return restored;
  }, [bump]);

  const reset = useCallback(
    (doc: LegalDocument) => {
      pastRef.current = [];
      futureRef.current = [];
      setCurrent(doc);
      bump();
    },
    [bump]
  );

  return {
    current,
    canUndo: pastRef.current.length > 0,
    canRedo: futureRef.current.length > 0,
    push,
    undo,
    redo,
    reset,
  };
}
